/**
 * WBM_PLATFORM / billingPlatform() — runtime detection of where the
 * subscription transacts.
 *
 * Web → Stripe (full controls in-app).
 * iOS → Apple IAP (store-managed; we hide in-app cancel/change/update).
 * Android → Google Play Billing (same as iOS).
 *
 * The store-managed cases require us to surface a "Manage in the
 * {store}" notice and HIDE our local payment-method, change-plan, and
 * cancel controls — Apple/Google reject apps that try to drive their
 * own subscription UI on top of theirs.
 */

export type Platform = "web" | "ios" | "android";

export interface BillingPlatform {
  id: Platform;
  rail: "Stripe" | "Apple" | "Google Play";
  /** When true, in-app cancel/change/update controls are allowed. */
  manages: boolean;
  /** The store name for the user-facing "manage there" copy. */
  store: string | null;
  /** The store-side path for the user-facing copy. */
  path: string | null;
}

const PLATFORMS: Record<Platform, BillingPlatform> = {
  web: {
    id: "web", rail: "Stripe", manages: true, store: null, path: null,
  },
  ios: {
    id: "ios", rail: "Apple", manages: false,
    store: "the App Store",
    path: "Settings ▸ your name ▸ Subscriptions",
  },
  android: {
    id: "android", rail: "Google Play", manages: false,
    store: "Google Play",
    path: "Play Store ▸ Payments & subscriptions",
  },
};

function detectPlatform(): Platform {
  // Capacitor is the project's native wrapper (per CLAUDE.md). When
  // running in the Capacitor shell, Capacitor.getPlatform() returns
  // "ios" | "android". On the web build it returns "web" or the global
  // is undefined.
  type CapacitorLike = {
    getPlatform?: () => string;
    isNativePlatform?: () => boolean;
  };
  const cap = (globalThis as unknown as { Capacitor?: CapacitorLike }).Capacitor;
  if (cap?.getPlatform) {
    const platform = cap.getPlatform();
    if (platform === "ios") return "ios";
    if (platform === "android") return "android";
  }
  return "web";
}

let _platform: Platform | null = null;

export function billingPlatform(): BillingPlatform {
  if (_platform === null) {
    _platform = detectPlatform();
  }
  return PLATFORMS[_platform];
}

/** Test/dev override — flip the detected platform without restarting. */
export function _setPlatformForTesting(p: Platform): void {
  _platform = p;
}

/**
 * Where THIS family's plan can be changed, from where the grown-up is now
 * (ipad-native-shell 10.5). The device decides what may be shown (no Stripe
 * controls in the App Store build); the plan's owner decides what is true.
 *
 * - `here`: web, a Stripe (or no) plan — the in-app controls work.
 * - `store_here`: the app, an App Store (or no) plan — the store manages it.
 * - `app_store_elsewhere`: web, but the plan was bought in the iPad app — the
 *   web's cancel/change would only touch our row, so send them to Apple.
 * - `web_elsewhere`: the app, but the plan was set up on the website — it is
 *   already unlocked here; it is managed there.
 */
export type SubscriptionRail = "here" | "store_here" | "app_store_elsewhere" | "web_elsewhere";

export function subscriptionRail(
  plat: BillingPlatform,
  provider: "stripe" | "apple" | null | undefined,
): SubscriptionRail {
  if (plat.manages) return provider === "apple" ? "app_store_elsewhere" : "here";
  return provider === "stripe" ? "web_elsewhere" : "store_here";
}
